import type { WordPressSite } from '../../api';
import type { Level, SetupProgress } from './projectHealth';

/**
 * The WordPress counterpart of projectHealth: what a WordPress project's
 * header, Overview and attention list say, derived only from what
 * inspection, the software check and the monitors actually recorded.
 * Nothing here guesses — a fact that was never read is "not known".
 */

/** setup for a WordPress environment: the site is placed once it is
 * connected; inspection and repository access are what make it usable. */
export function wordpressSetup(input: {
  repositoryUrl: string;
  repositoryAccessConfigured: boolean;
  site: WordPressSite | null;
}): SetupProgress {
  const site = input.site;
  const step = (key: string, label: string, todo: string, done: boolean): SetupProgress['steps'][number] => ({ key, label, todo, done, state: done ? 'done' : 'todo' });
  const steps = [
    step('repository', 'Repository set', 'Set the repository', input.repositoryUrl.trim() !== ''),
    step('access', 'Repository access configured', 'Give Commerce Ops read access to the repository', input.repositoryAccessConfigured),
    step('site', 'WordPress site connected', 'Connect the WordPress site', Boolean(site)),
    step('inspected', 'Site inspected', 'Inspect the site', Boolean(site?.inspection) && !site?.inspection_error),
  ];
  const done = steps.filter((step) => step.done).length;
  return {
    steps,
    done,
    total: steps.length,
    complete: Boolean(site),
    next: steps.find((step) => !step.done)?.todo ?? null,
    nextKey: steps.find((step) => !step.done)?.key ?? null,
    planLifecycle: null,
    runAction: null,
  };
}

/** The header's version facts: "WordPress 7.1.2", "WooCommerce 11.1.2". */
export function wordpressVersionFacts(site: WordPressSite | null): string[] {
  const facts: string[] = [];
  const wp = site?.inspection?.site.wp_version || site?.inspection?.inventory.core_version;
  if (wp) facts.push(`WordPress ${wp}`);
  const woo = site?.inspection?.site.woocommerce?.version;
  if (woo) facts.push(`WooCommerce ${woo}`);
  return facts;
}

const severityRank: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1, info: 0 };

/** componentLabel turns the log attribution ("plugin:acme-shipping") into words. */
export function componentLabel(component: string | undefined): string {
  if (!component || component === 'unknown') return '';
  if (component === 'core') return 'WordPress core';
  if (component === 'mu-plugin') return 'a must-use plugin';
  const [kind, slug] = component.split(':', 2);
  if (!slug) return component;
  if (kind === 'plugin') return `the ${slug} plugin`;
  if (kind === 'theme') return `the ${slug} theme`;
  if (kind === 'mu_plugin' || kind === 'mu-plugin') return `the ${slug} must-use plugin`;
  if (kind === 'core') return 'WordPress core';
  return component;
}

/**
 * What a software or security check could not cover. A check that did not
 * run completely cannot say "up to date": its level is "not known" unless it
 * already found something, and the reason is shown with it (CO-184).
 */
export interface CheckCoverage {
  complete: boolean;
  /** What was not covered, in a sentence; empty when everything was. */
  gap: string;
}

/** softwareCoverage says whether the updates check also covered known vulnerabilities. */
export function softwareCoverage(site: WordPressSite | null): CheckCoverage {
  const report = site?.software;
  if (!report) return { complete: false, gap: 'Software check not run' };
  if (report.vulnerability_state === 'unavailable') {
    return { complete: false, gap: `Vulnerability feed unavailable${report.vulnerability_error ? `: ${report.vulnerability_error}` : ''}` };
  }
  if (report.vulnerability_state !== 'checked') {
    return { complete: false, gap: 'Vulnerabilities not checked: no feed configured' };
  }
  return { complete: true, gap: '' };
}

/** securityCoverage says whether every security check ran. */
export function securityCoverage(site: WordPressSite | null): CheckCoverage {
  if (!site?.security) return { complete: false, gap: 'Security check not run' };
  const errors = site.security.errors ?? [];
  if (errors.length > 0) return { complete: false, gap: `Some checks could not run: ${errors.join('; ')}` };
  return { complete: true, gap: '' };
}

/** updatesLevel rolls the software report into one level for a summary card. */
export function updatesLevel(site: WordPressSite | null): Level {
  const summary = site?.software?.summary;
  if (!summary) return 'unknown';
  if (summary.vulnerable > 0) return 'critical';
  if (summary.security_updates > 0 || summary.updates_available > 0) return 'warning';
  // Nothing found is "ok" only when the check covered everything it should.
  return softwareCoverage(site).complete ? 'ok' : 'unknown';
}

/** securityLevel rolls the security report into one level. */
export function securityLevel(site: WordPressSite | null): Level {
  const findings = site?.security?.findings;
  if (!site?.security) return 'unknown';
  const worst = Math.max(0, ...(findings ?? []).map((finding) => severityRank[finding.severity] ?? 0));
  if (worst >= 4) return 'critical';
  if (worst >= 2) return 'warning';
  return securityCoverage(site).complete ? 'ok' : 'unknown';
}

/** siteLevel is whether the site itself loaded at the last inspection. */
export function siteLevel(site: WordPressSite | null): Level {
  if (!site?.inspection) return 'unknown';
  if (site.inspection_error) return 'warning';
  return site.inspection.site.bootstrapped ? 'ok' : 'critical';
}

/** The capability chips a site shows, in reading order. Unknown stays unknown. */
/** `neutral` rows are plain facts (multisite or not) and carry no status badge. */
export function capabilityRows(
  site: WordPressSite | null,
): { key: string; label: string; value: string; level: Level; neutral?: boolean }[] {
  const caps = site?.inspection?.capabilities;
  if (!caps) return [];
  const yes = (on: boolean): Level => (on ? 'ok' : 'unknown');
  return [
    { key: 'wp_cli', label: 'WP-CLI', value: caps.wp_cli ? 'Available' : 'Not found', level: caps.wp_cli ? 'ok' : 'critical' },
    { key: 'access', label: 'Access', value: caps.agent ? 'Commerce Ops agent' : caps.ssh ? 'SSH' : 'Not known', level: caps.agent || caps.ssh ? 'ok' : 'unknown' },
    { key: 'woocommerce', label: 'WooCommerce', value: caps.woocommerce ? 'Active' : 'Not active', level: yes(caps.woocommerce) },
    { key: 'multisite', label: 'Multisite', value: caps.multisite ? 'Yes' : 'No', level: 'ok', neutral: true },
    { key: 'bedrock', label: 'Bedrock layout', value: caps.bedrock ? 'Yes' : 'No', level: 'ok', neutral: true },
    { key: 'composer', label: 'Composer', value: caps.composer ? 'Available' : 'Not found', level: yes(caps.composer) },
    { key: 'node', label: 'Node.js', value: caps.node ? 'Available' : 'Not found', level: yes(caps.node) },
    { key: 'object_cache', label: 'Object cache', value: cacheWord(caps.object_cache), level: 'ok', neutral: true },
    { key: 'page_cache', label: 'Page cache', value: cacheWord(caps.page_cache), level: 'ok', neutral: true },
    {
      key: 'cron',
      label: 'WP-Cron',
      value: cronWord(caps.cron_mode, caps.system_cron),
      level: caps.cron_mode === 'external_or_unknown' ? 'warning' : caps.cron_mode === 'unknown' ? 'unknown' : 'ok',
    },
    {
      key: 'deploy',
      label: 'Release switch',
      value: caps.atomic_symlink ? 'Atomic symlink' : caps.filesystem_deploy ? 'Directory swap' : 'Not possible',
      level: caps.atomic_symlink || caps.filesystem_deploy ? 'ok' : 'critical',
    },
    {
      key: 'logs',
      label: 'Error logs',
      value: caps.logs === 'full' ? 'Readable' : caps.logs === 'partial' ? 'Partly readable' : 'Not readable',
      level: caps.logs === 'full' ? 'ok' : caps.logs === 'partial' ? 'warning' : 'unknown',
    },
    { key: 'action_scheduler', label: 'Action Scheduler', value: caps.action_scheduler ? 'Present' : 'Not present', level: 'ok', neutral: true },
  ];
}

const cacheWords: Record<string, string> = {
  none: 'None',
  redis: 'Redis',
  memcached: 'Memcached',
  apcu: 'APCu',
  litespeed: 'LiteSpeed Cache',
  wp_rocket: 'WP Rocket',
  w3_total_cache: 'W3 Total Cache',
  wp_super_cache: 'WP Super Cache',
  sg_optimizer: 'SiteGround Optimizer',
  breeze: 'Breeze',
  other: 'Other (detected, not named)',
};

export function cacheWord(value: string | undefined): string {
  if (!value) return 'Not known';
  return cacheWords[value] ?? value;
}

const cronModeWords: Record<string, string> = {
  wp_cron: 'Runs on page visits',
  system_cron_wp_cron_php: 'System scheduler calls wp-cron.php',
  system_cron_wp_cli: 'System scheduler runs WP-CLI',
  external_or_unknown: 'Page-visit cron is off; no scheduler found',
  unknown: 'Not known',
};

export function cronWord(mode: string, systemCron: string): string {
  const base = cronModeWords[mode] ?? 'Not known';
  if (systemCron === 'unknown' && mode !== 'unknown') return `${base} (crontab not readable)`;
  return base;
}
